import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import TermsOfServicePage from './components/TermsOfServicePage';
import AuthForm from './components/AuthForm';
import AuthCallback from './components/AuthCallback';
import Navigation from './components/Navigation';
import HomeView from './components/HomeView';
import ExploreView from './components/ExploreView';
import CreatePost from './components/CreatePost';
import SavedView from './components/SavedView';
import ProfileView from './components/ProfileView';
import MessagesView from './components/MessagesView';
import CollaborationsView from './components/CollaborationsView';
import EventsView from './components/EventsView';
import AnalyticsView from './components/AnalyticsView';
import SalesView from './components/SalesView';
import HelpView from './components/HelpView';
import NotificationsView from './components/NotificationsView';
import ResetPasswordForm from './components/ResetPasswordForm';
import FollowingView from './components/FollowingView';
import AdminView from './components/AdminView';
import ReportDetailView from './components/ReportDetailView';
import AdsManagerView from './components/AdsManagerView';
import PostDetailView from './components/PostDetailView';
import SuspendedAccountView from './components/SuspendedAccountView';
import DiditCallback from './components/DiditCallback';
import { Eye, Undo2 } from 'lucide-react';
import { supabase } from './lib/supabase';
import { profileService } from './services/profileService';
import { User } from './types';
import { useLanguage } from './context/LanguageContext';

interface RoleGuardProps {
  allowed: Array<'artist' | 'visitor' | 'moderator' | 'admin'>;
  children: React.ReactElement;
}

const RoleGuard: React.FC<RoleGuardProps> = ({ allowed, children }) => {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (!allowed.includes(user.user_type)) {
    return <Navigate to="/home" replace />;
  }

  return children;
};

const AppContent: React.FC = () => {
  const { user, loading, refreshUser } = useAuth();
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [impersonatingUser, setImpersonatingUser] = useState<User | null>(null);
  const [originalAdminToken, setOriginalAdminToken] = useState<string | null>(null);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // Function to handle reverting impersonation
  const handleRevertImpersonation = async () => {
    const storedOriginalToken = localStorage.getItem('originalAdminToken');
    if (!storedOriginalToken) {
      console.error('No original admin session found to revert to.');
      return;
    }

    try {
      const { error: setSessionError } = await supabase.auth.setSession({
        access_token: storedOriginalToken,
        refresh_token: storedOriginalToken,
      });

      if (setSessionError) {
        throw setSessionError;
      }

      localStorage.removeItem('originalAdminToken');
      setOriginalAdminToken(null);
      setImpersonatingUser(null);
      if (refreshUser) refreshUser(); // Refresh user context to show original admin
      console.log('Successfully reverted to original admin session.');
    } catch (err) {
      console.error('Error reverting session:', err);
    }
  };

  useEffect(() => {
    // Check for existing impersonation on component mount
    const storedOriginalToken = localStorage.getItem('originalAdminToken');
    if (storedOriginalToken) {
      setOriginalAdminToken(storedOriginalToken);
      const fetchImpersonatedUser = async () => {
        try {
          const { data: { user: currentUser } } = await supabase.auth.getUser();
          if (currentUser) {
            const profile = await profileService.getProfile(currentUser.id);
            setImpersonatingUser(profile);
          }
        } catch (err) {
          console.error('Error fetching impersonated user profile on App mount:', err);
          localStorage.removeItem('originalAdminToken');
          setOriginalAdminToken(null);
          setImpersonatingUser(null);
        }
      };
      fetchImpersonatedUser();
    }

    // Custom event listener for internal navigation (e.g., from PostCard)
    const handleNavigate = (event: CustomEvent) => {
      const { path, state } = event.detail;
      if (path) {
        navigate(path, { state });
      }
    };

    const handleOpenCreatePost = () => {
      setShowCreatePost(true);
    };

    window.addEventListener('navigate', handleNavigate as EventListener);
    window.addEventListener('openCreatePost', handleOpenCreatePost as EventListener);

    return () => {
      window.removeEventListener('navigate', handleNavigate as EventListener);
      window.removeEventListener('openCreatePost', handleOpenCreatePost as EventListener);
    };
  }, [user, refreshUser, navigate]);

  useEffect(() => {
    console.log('📍 Navigation event', {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    });
  }, [location]);

  useEffect(() => {
    // Handler for push token from mobile app
    const handlePushToken = async (token: string) => {
      if (user?.id) {
        try {
          await profileService.updateProfile(user.id, { expo_push_token: token });
          console.log('Push token updated for user:', user.id);
        } catch (error) {
          console.error('Error updating push token:', error);
        }
      } else {
        localStorage.setItem('expo_push_token', token);
      }
    };

    const handleMessage = (event: MessageEvent) => {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data?.type === 'EXPO_PUSH_TOKEN' && data?.token) {
          handlePushToken(data.token);
        }
      } catch {
        // Ignore parsing errors
      }
    };

    window.addEventListener('message', handleMessage);
    
    const storedToken = localStorage.getItem('expo_push_token');
    if (storedToken && user?.id) {
       handlePushToken(storedToken);
    }

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('app.loading', 'Cargando...')}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/politica-privacidad" element={<PrivacyPolicyPage />} />
        <Route path="/condiciones-legales" element={<TermsOfServicePage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/reset-password" element={<ResetPasswordForm />} />
        <Route path="/didit-callback" element={<DiditCallback />} />
        <Route path="*" element={<AuthForm />} />
      </Routes>
    );
  }

  // Check if user account is suspended, warned, or banned
  const isAccountRestricted = () => {
    if (!user.account_status) return false;

    if (user.account_status === 'banned') return true;
    if (user.account_status === 'warned') return true;

    if (user.account_status === 'suspended') {
      if (!user.suspended_until) return true;
      return new Date(user.suspended_until) > new Date();
    }

    return false;
  };

  if (isAccountRestricted()) {
    return (
      <Routes>
        <Route path="*" element={<SuspendedAccountView />} />
      </Routes>
    );
  }

  const handlePostCreated = () => {
    setShowCreatePost(false);
    navigate('/home'); // Navigate to home or explore after post creation
  };

  const isFullScreenRoute = location.pathname === '/reset-password';

  return (
    <div className="min-h-screen bg-gray-50">
      {impersonatingUser && originalAdminToken && (
        <div className="fixed top-0 left-0 right-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-3 text-center text-sm font-medium z-50 shadow-lg flex items-center justify-center gap-3">
          <Eye size={18} />
          <span>
            {t('app.impersonatingPrefix', 'Impersonalizando a')}{' '}
            <span className="font-bold">
              {impersonatingUser.full_name} (@{impersonatingUser.username})
            </span>
          </span>
          <button
            onClick={handleRevertImpersonation}
            className="ml-4 px-3 py-1 bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-colors flex items-center gap-1"
          >
            <Undo2 size={16} />
            {t('app.revert', 'Revertir')}
          </button>
        </div>
      )}
      {!isFullScreenRoute && <Navigation onCreateClick={() => setShowCreatePost(true)} />}

      <div className={`${!isFullScreenRoute ? 'lg:ml-64 pb-16 lg:pb-0' : ''} ${impersonatingUser ? 'pt-14' : ''}`}>
        <main className="min-h-screen">
          <Routes>
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/politica-privacidad" element={<PrivacyPolicyPage />} />
            <Route path="/condiciones-legales" element={<TermsOfServicePage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/reset-password" element={<ResetPasswordForm />} />
            <Route path="/didit-callback" element={<DiditCallback />} />
            <Route path="/home" element={<HomeView />} />
            <Route path="/explore" element={<ExploreView />} />
            <Route path="/saved" element={<SavedView />} />
            <Route path="/profile" element={<ProfileView key="own-profile" />} /> {/* Own profile */}
            <Route path="/profile/:username" element={<ProfileView key="user-profile" />} /> {/* Other user's profile */}
            <Route path="/post/:postId" element={<PostDetailView />} /> {/* Post detail page */}
            <Route path="/collaborations" element={<CollaborationsView />} />
            <Route path="/messages" element={<MessagesView />} />
            <Route path="/messages/:conversationId" element={<MessagesView />} />
            <Route path="/events" element={<EventsView />} />
            <Route path="/analytics" element={<AnalyticsView />} />
            <Route path="/sales" element={<SalesView />} />
            <Route path="/help" element={<HelpView />} />
            <Route path="/notifications" element={<NotificationsView />} />
            <Route path="/community" element={<FollowingView />} />
            <Route
              path="/admin"
              element={
                <RoleGuard allowed={['admin', 'moderator']}>
                  <AdminView />
                </RoleGuard>
              }
            />
            <Route
              path="/reports/:reportId"
              element={
                <RoleGuard allowed={['admin', 'moderator']}>
                  <ReportDetailView />
                </RoleGuard>
              }
            />
            <Route
              path="/ads"
              element={
                <RoleGuard allowed={['admin', 'artist']}>
                  <AdsManagerView />
                </RoleGuard>
              }
            />
            <Route path="/" element={<HomeView />} />
            <Route path="*" element={<HomeView />} />
          </Routes>
        </main>
      </div>

      {showCreatePost && (
        <CreatePost
          onClose={() => setShowCreatePost(false)}
          onSubmit={handlePostCreated}
        />
      )}
    </div>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
